import { useEffect, useRef, useState } from 'react';

export default function About() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [counts, setCounts] = useState({ years: 0, specialists: 0, clients: 0, cities: 0 });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      const duration = 2000;
      const steps = 60;
      const interval = duration / steps;

      const targets = { years: 20, specialists: 15, clients: 5000, cities: 13 };

      let step = 0;
      const timer = setInterval(() => {
        step++;
        const progress = step / steps;

        setCounts({
          years: Math.floor(targets.years * progress),
          specialists: Math.floor(targets.specialists * progress),
          clients: Math.floor(targets.clients * progress),
          cities: Math.floor(targets.cities * progress),
        });

        if (step >= steps) {
          clearInterval(timer);
          setCounts(targets);
        }
      }, interval);

      return () => clearInterval(timer);
    }
  }, [isVisible]);

  const values = [
    {
      icon: 'ri-heart-pulse-line',
      title: 'العناية الشخصية',
      description: 'نهتم بكل عميل بشكل فردي ونقدم خدمة مخصصة تناسب احتياجاته',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'الخصوصية والأمان',
      description: 'نضمن خصوصية عملائنا التامة في جميع مراحل الخدمة',
    },
    {
      icon: 'ri-award-line',
      title: 'الجودة والاحترافية',
      description: 'نلتزم بأعلى معايير الجودة في تقديم خدماتنا السمعية',
    },
    {
      icon: 'ri-hand-heart-line',
      title: 'التفهّم الكامل',
      description: 'نتفهم كافة ظروف عملائنا الخاصة ونتعامل معها بأفضل الأساليب',
    },
  ];

  const highlights = [
    { icon: 'ri-home-heart-line', text: 'نصل إليك في منزلك أو مكان عملك' },
    { icon: 'ri-user-star-line', text: 'أخصائيون سعوديون معتمدون ذوو خبرة وكفاءة' },
    { icon: 'ri-map-pin-2-line', text: 'نغطي 13 مدينة في جميع أنحاء المملكة' },
    { icon: 'ri-time-line', text: 'مواعيد مرنة تناسب جدولك اليومي' },
  ];

  return (
    <section id="about" ref={sectionRef} className="py-24 bg-white">
      <div className="container mx-auto px-4" style={{ maxWidth: '1140px' }}>
        {/* Header Section */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <div
            className={`inline-block bg-[#184187]/10 px-5 py-2 rounded-full mb-4 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <span className="text-[#184187] font-semibold text-sm">تعرّف علينا</span>
          </div>
          <h2
            className={`text-5xl md:text-6xl font-bold text-gray-900 mb-6 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            نبذة عنّا
          </h2>
          <p
            className={`text-lg text-gray-600 leading-relaxed transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            نخبة من الأخصائيين والاستشاريين السعوديين المميزين يسعدون بتقديم تجربة فريدة لاختبارات السمع وخدمات السماعات الطبية، مباشرة في منزلك أو مكان عملك. نفهم صعوبة خروج بعض العملاء، خاصة كبار السن أو من لديهم ظروف خاصة، لذلك قررنا أن تتجه العيادات إليك أينما كنت.
          </p>
        </div>

        {/* Vision, Mission & Image Grid */}
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20 transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Left Side - Vision & Mission Cards */}
          <div className="space-y-6">
            {/* Vision Card */}
            <div className="bg-[#184187] text-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="ri-eye-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold">رؤيتنا</h3>
              </div>
              <p className="text-white/90 leading-relaxed">
                أن نكون الخيار الأول في المملكة لخدمات السمع المنزلية، من خلال تقديم رعاية صحية متميزة تصل إلى كل منزل، مع الحفاظ على أعلى معايير الجودة والاحترافية.
              </p>
            </div>

            {/* Mission Card - changed to slate/dark blue */}
            <div className="bg-slate-700 text-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="ri-flag-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold">رسالتنا</h3>
              </div>
              <p className="text-white/90 leading-relaxed">
                تقديم خدمات سمعية احترافية في منازل عملائنا، من خلال فريق من الأخصائيين السعوديين المؤهلين، مع ضمان الخصوصية والراحة، والمساهمة في تحسين جودة حياة من يعانون من مشاكل السمع.
              </p>
            </div>

            {/* Highlights strip - fills the gap below mission card */}
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
              <h4 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                <i className="ri-star-line text-[#184187]"></i>
                لماذا سماعتي؟
              </h4>
              <div className="space-y-3">
                {highlights.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#184187]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-[#184187] text-base`}></i>
                    </div>
                    <span className="text-sm text-gray-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Side - Large Image */}
          <div className="relative h-full min-h-[500px] rounded-2xl overflow-hidden shadow-xl group">
            <img
              src="https://public.readdy.ai/ai/img_res/5864a67b3accc85444623b7c35a77e7f.jpg"
              alt="خدمة سماعتي المنزلية"
              className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#184187]/30 to-transparent"></div>
          </div>
        </div>

        {/* Values Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3
              className={`text-4xl md:text-5xl font-bold text-gray-900 mb-4 transition-all duration-1000 delay-400 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              قيمنا
            </h3>
            <p
              className={`text-lg text-gray-600 transition-all duration-1000 delay-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              المبادئ التي نؤمن بها ونعمل وفقها كل يوم
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={index}
                className={`bg-gray-50 rounded-2xl p-6 text-center hover:bg-[#184187] hover:text-white group transition-all duration-500 hover:-translate-y-2 hover:shadow-xl cursor-default ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${500 + index * 100}ms` }}
              >
                <div className="w-16 h-16 bg-[#184187]/10 group-hover:bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-all duration-500">
                  <i className={`${value.icon} text-3xl text-[#184187] group-hover:text-white transition-colors duration-500`}></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 group-hover:text-white mb-2 transition-colors duration-500">
                  {value.title}
                </h4>
                <p className="text-sm text-gray-600 group-hover:text-white/90 leading-relaxed transition-colors duration-500">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Statistics Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: 'ri-verified-badge-line', value: 'خبرة وكفاءة', label: 'أخصائيون مؤهلون', delay: 600, isText: true },
            { icon: 'ri-map-pin-line', value: `${counts.cities}+`, label: 'مدينة نخدمها', delay: 700 },
            { icon: 'ri-user-heart-line', value: `${counts.clients.toLocaleString()}+`, label: 'عميل راضٍ', delay: 800 },
            { icon: 'ri-team-line', value: `${counts.specialists}+`, label: 'أخصائي مؤهل', delay: 900 },
          ].map((stat, index) => (
            <div
              key={index}
              className={`text-center bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all duration-500 hover:-translate-y-1 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${stat.delay}ms` }}
            >
              <div className="w-16 h-16 mx-auto mb-4 bg-[#184187] rounded-full flex items-center justify-center">
                <i className={`${stat.icon} text-3xl text-white`}></i>
              </div>
              <div className={`font-bold text-[#184187] mb-2 ${'isText' in stat && stat.isText ? 'text-2xl' : 'text-4xl'}`}>{stat.value}</div>
              <div className="text-gray-600 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
